import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { LoggerService } from 'app/core/logger/logger.service';
import { TutorService } from 'app/core/services/tutor.service';
import { UserService } from 'app/core/user/user.service';
import { hasRole } from 'app/core/user/user-role.utils';
import { Observable, catchError, map, of, switchMap } from 'rxjs';

/**
 * Landing por defecto del usuario autenticado.
 *
 * Hasta el issue #17 la raíz y `signed-in-redirect` mandaban a `/example`
 * (la página de demostración de la plantilla Fuse) a todo el mundo. Ahora
 * el tutor cae en su dashboard.
 *
 * Issue #17: **solo cambia el caso del tutor**. `/example` sigue siendo la
 * landing por defecto de todos los demás roles — resolver el destino de
 * alumnos y admins es otra discusión y no se abre acá.
 *
 * Issue #128: el tutor que todavía no compró ningún paquete y no tiene
 * alumnos a cargo va a la selección de paquete (`/family-registration`) en
 * vez del dashboard. En orden, y cortando en el primero que alcance:
 * - `parentInfo.students` con alumnos en el usuario cargado → dashboard, sin
 *   pedir nada. Sólo viene de `GET auth/me`: justo después del login el
 *   usuario es el de la respuesta del login, que no lo trae.
 * - `TutorService.hasAnyPackage()`: un pago pendiente, aprobado o rechazado
 *   cuenta como «tiene paquete» → dashboard.
 * - Sin paquete: `TutorService.hasLinkedStudents()` (las dos vías de la API,
 *   para la familia que cargó el admin) → dashboard si tiene alguno. Se
 *   pregunta aunque el usuario traiga `parentInfo` vacío: un alumno puede
 *   estar vinculado sólo por la colección TutorStudent.
 * Si cualquier consulta falla, el tutor va al dashboard como antes: no se
 * traba el ingreso.
 *
 * El rol sale de `GET auth/me`, que `AuthService.check()` carga antes de
 * dar true: se lee `UserService.currentUser` justo después, así que no se
 * puede resolver con un `redirectTo` sincrónico (issue #82).
 *
 * Devuelve siempre un UrlTree; nunca deja activar la ruta a la que está
 * puesto.
 */
export const signedInRedirectGuard: CanActivateFn = (): Observable<UrlTree> => {
    const router = inject(Router);
    const userService = inject(UserService);
    const authService = inject(AuthService);
    const tutorService = inject(TutorService);
    const logger = inject(LoggerService);

    const fallback = router.parseUrl('/example');
    const dashboard = router.parseUrl('/dashboard');

    return authService.check().pipe(
        switchMap((authenticated) => {
            const user = userService.currentUser;

            // Sin sesión (o si se cerró entretanto) no hay rol que consultar:
            // se mantiene el destino de siempre y el AuthGuard de esa ruta
            // manda al sign-in.
            if (!authenticated || !user) {
                return of(fallback);
            }

            if (!hasRole(user, 'parent')) {
                return of(fallback);
            }

            if (user.parentInfo?.students?.length) {
                return of(dashboard);
            }

            return tutorService.hasAnyPackage().pipe(
                switchMap((hasPackage) => (hasPackage ? of(true) : tutorService.hasLinkedStudents())),
                map((hasPackageOrStudents) =>
                    hasPackageOrStudents ? dashboard : router.parseUrl('/family-registration')
                ),
                catchError((error) => {
                    logger.warn('No se pudo saber si el tutor tiene paquetes o alumnos; va al dashboard', error);
                    return of(dashboard);
                })
            );
        })
    );
};
